import {Component, OnInit, ViewChild, ElementRef, OnDestroy} from '@angular/core';
import {AuthService} from '../../core/auth.service';

import {ActivatedRoute, Router} from '@angular/router';

import {LoginForm, UserType} from '../../../models/user';

import 'rxjs/add/observable/throw';

import {TranslateService} from '@ngx-translate/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {PlaceholderVisibilityService} from '../../services/placeholder-visibility/placeholder-visibility.service';
import {CommonConstant} from '../../app.constants';
import {UserService} from '../../services/user.service';
import {EventTrackerService, Page, UserActionType, ActivePages} from '../../services/event-tracker/event-tracker.service';
import {AppSettings} from '../../utils/app-settings';
import { WebUtils } from '../../utils/web.utils';

export enum ReturnUrlRoute {
  DISCOVER = 'discover',
  ADMIN = 'framed/admin'
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit, OnDestroy {
  private readonly fallbackUtm = {
    utm_source: 'login',
    utm_medium: 'organic',
    utm_campaign: 'organic'
  };
  user: FormGroup;
  private returnUrl: string;
  errorMessage: string;
  // this flag is used to know when we should send the login action event to Segment. Prevent duplicate event
  private canTrackLoginAction = false;

  @ViewChild('userNameInput') private _userNameInput: ElementRef;
  @ViewChild('passwordInput') private _passwordInput: ElementRef;

  constructor(
    private auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private placeholderService: PlaceholderVisibilityService,
    private userService: UserService,
    private eventTracker: EventTrackerService) {}

  ngOnInit() {
    WebUtils.loadHubspotChatWidget();

    this.user = new FormGroup({
      username: new FormControl(null, [
        Validators.required,
        Validators.email
      ]),
      password: new FormControl(null, [
        Validators.required
      ])
    });

    let returnUrl: string = ReturnUrlRoute.DISCOVER;
    const targetUrl = this.route.snapshot.queryParams['target_url'];

    if (targetUrl) {
      const decodedUrl = decodeURIComponent(targetUrl);

      // avoid circular navigation caused by '/login' router
      // redirect by url /login again won't trigger guard
      if (decodedUrl !== '/login' && decodedUrl !== 'undefined') {
        returnUrl = decodedUrl;
      }
    }

    this.returnUrl = returnUrl;
    this.errorMessage = '';
    this.user.valueChanges.subscribe((res) => {
      this.errorMessage = null;
    });
  }

  ngOnDestroy(): void {
    WebUtils.removeHubspotChatWidget();
  }

  forgotPassword() {
    this.auth.updateResetPassEmail(this.user.controls.username.value);
    return this.router.navigate(['/reset-password']);
  }

  onSubmit({ value, valid }: {value: LoginForm, valid: boolean}) {
    if (valid) {
      this.placeholderService.show(CommonConstant.LOGIN_PLACEHOLDER);
      this.auth.login(value.username, value.password, data => {
        this.canTrackLoginAction = true;
        this.auth.getUserData(user => {
          this.eventTracker.hasIdentifiedObserver.subscribe((res) => {
            if (res && this.canTrackLoginAction) {
              this.eventTracker.trackUserAction(UserActionType.LOGIN, Page.LOGIN, undefined, undefined, undefined, {
                type: ActivePages.LOGIN,
              });
              this.canTrackLoginAction = false;
            }
          });
          if (this.auth.getUser().type === UserType.ANALYST) {
            this.returnUrl = ReturnUrlRoute.ADMIN;
          }
          if (!this.userService.getCompanyId()) {
            this.userService.loadAccountListByUser(this.auth.getUser(), () => {
              this.router.navigateByUrl(this.returnUrl);
            });
          } else {
            this.router.navigateByUrl(this.returnUrl);
          }
        }, err => {
          this.errorMessage = this.translate.instant('LOGIN.server_error');
          this.auth.logout();
        });
        this.placeholderService.hide(CommonConstant.LOGIN_PLACEHOLDER);
      }, error => {
        if (error.error && error.error.data === 'invalid_credential') {
          this.errorMessage = this.translate.instant('LOGIN.email_not_verified');
        } else if (error.error && error.error.data === 'sso_user_not_allowed') {
          this.errorMessage = this.translate.instant('LOGIN.sso_user', { mmiLoginLink: AppSettings.mmiLoginLink() });
        } else if (error.status === 400) {
          this.errorMessage = this.translate.instant('LOGIN.wrong_username_password');
        } else {
          this.errorMessage = this.translate.instant('LOGIN.server_error');
        }
        this.placeholderService.hide(CommonConstant.LOGIN_PLACEHOLDER);
      });

      return;
    }

    const form = this.user;

    if (!form.controls.username.valid) {
      this.showErrorMessageOnInput(this._userNameInput, 'LOGIN.invalid_email');
      return;
    }

    if (!form.controls.password.valid) {
      this.showErrorMessageOnInput(this._passwordInput, 'LOGIN.password_required');
      return;
    }
  }

  private showErrorMessageOnInput(inputElementRef: ElementRef, messageKey: string) {
    const inputElement = inputElementRef.nativeElement;

    if (!inputElement) {
      return;
    }

    const message = this.translate.instant(messageKey);
    inputElement.focus();
    inputElement.setCustomValidity(message);
    inputElement.reportValidity();
  }

  get signUpLink() {
    const utm_source = this.getUtmData('utm_source');
    const utm_campaign = this.getUtmData('utm_campaign');
    const utm_medium = this.getUtmData('utm_medium');
    return `${AppSettings.signUpLink()}?utm_source=${utm_source}&utm_campaign=${utm_campaign}&utm_medium=${utm_medium}`;
  }

  private getUtmData(utmKey) {
    try {
      return localStorage.getItem(utmKey) || this.fallbackUtm[utmKey];
    } catch (error) {
      return this.fallbackUtm[utmKey];
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/auth/login/login.component.ts